import { QrCode, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { KioskLanguage } from "@/lib/i18n";

interface GuideQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  language: KioskLanguage;
}

const GUIDE_COPY: Record<
  KioskLanguage,
  {
    defaultTitle: string;
    instruction: string;
    scanHint: string;
    close: string;
  }
> = {
  gl: {
    defaultTitle: "Guía interactiva",
    instruction:
      "Escanea o código QR co teu dispositivo móbil para descargar a guía e consultala durante a visita.",
    scanHint: "Apunta a cámara do móbil cara ao código",
    close: "Pechar",
  },
  es: {
    defaultTitle: "Guía interactiva",
    instruction:
      "Escanea el código QR con tu dispositivo móvil para descargar la guía y consultarla durante tu visita.",
    scanHint: "Apunta la cámara de tu móvil hacia el código",
    close: "Cerrar",
  },
  en: {
    defaultTitle: "Interactive guide",
    instruction:
      "Scan the QR code with your mobile device to download the guide and use it during your visit.",
    scanHint: "Point your phone camera at the code",
    close: "Close",
  },
};

const resolveGuideUrl = (url: string): string => {
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
};

export const GuideQrModal = ({
  isOpen,
  onClose,
  url,
  title,
  language,
}: GuideQrModalProps) => {
  const copy = GUIDE_COPY[language];
  const resolvedUrl = resolveGuideUrl(url);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="guide-qr-dialog max-w-3xl border-0 p-0">
        <div className="guide-qr-header">
          <div className="guide-qr-heading-icon" aria-hidden="true">
            <QrCode />
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="guide-qr-title">
              {title || copy.defaultTitle}
            </DialogTitle>
            <DialogDescription className="guide-qr-description">
              {copy.instruction}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="guide-qr-body">
          <div className="guide-qr-code" aria-label={copy.scanHint}>
            {resolvedUrl && (
              <QRCodeSVG
                value={resolvedUrl}
                size={340}
                level="M"
                marginSize={3}
                bgColor="#ffffff"
                fgColor="#063f37"
              />
            )}
          </div>

          <div className="guide-qr-hint">
            <Smartphone aria-hidden="true" />
            <span>{copy.scanHint}</span>
          </div>

          <div className="guide-qr-actions">
            <Button
              type="button"
              className="guide-qr-close-button"
              onClick={onClose}
            >
              {copy.close}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
